import { Grid, IconButton, InputAdornment, Typography } from '@mui/material'; 
import React, { memo, ReactElement, useContext, useEffect, useRef, useState } from 'react'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ChatbotContext, ChatbotContextPayload } from '../../../../../context/chatbotContext';
import { styled, alpha } from '@mui/material/styles';
import Menu, { MenuProps } from '@mui/material/Menu';
import styles from "../../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import { checkInternetConnection, getFileTypeImgIcon, getWhatsappMediaSizeLimit, hasInboxReadAccess, isChatbotTextDirectionLtr } from '../../../../../utils/constants';
import { currentActiveChatbotThreadTypeNew, manualAgentData, sendCannedResponsePayload, sendTemplatePayload, uploadedMedia } from '../../../../../models/axiosModel';
import GlobalPopUp from '../../../../../components/utility/globalPopUp';
import GlobalTooltip from '../../../../../components/utility/tooltip/globalTooltip';
import FileUploadPopup from './fileUploadPopup';
import agent from '../../../../../services/api';
import { useShallow } from 'zustand/react/shallow';
import { getThreadData, isUserChatExpired } from '../../../utils';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';
import { IntegrationContext, IntegrationContextPayload } from '../../../../../context/integrationContext';
import { useGenericStore } from '../../../../../store/genericStore';
import CannedResponse from './cannedResponse';
import { ATTACHMENT_TEMPLATE_LABEL_TEXT, fileType } from '../../../constants';
import useGenericHook from '../../../../../hooks/useGenericHook';

interface FileUploadInboxProps {
    getWATemplateList: (meta_integation_whatsapp_id: number, flagMarketingTemplateOnly?: boolean | undefined) => Promise<void>,
    handleSendMessageManually: (inputValue: string, flagMediaMessage: boolean, flagCannedResponseTemplate: boolean, flagTemplateMessage: boolean, selectedMediaSet: uploadedMedia[], sendTemplatePayload: sendTemplatePayload | null, cannedResponsePayload?: sendCannedResponsePayload) => Promise<void>,
    getManualOverrideConfig: (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => string,
}  

export interface fileTypeInterface {
    label: string, 
    icon: ReactElement, 
    acceptedFileType: string, 
}

const StyledMenu = styled((props: MenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
        }}
        transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(-1),
        minWidth: 150,
        color:
            theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
            'rgba(0, 0, 0, 0.01) 0px 0px 10px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
    },
}));

const FileUploadInbox: React.FC<FileUploadInboxProps> = (props) => { 
    const { handleSendMessageManually, getManualOverrideConfig, getWATemplateList} = props; 
    const { chatBotDetails } = useContext(ChatbotContext) as ChatbotContextPayload; 
    const { setFlagLoading, flagLoading, userDetail, handleError, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload

    const { integrationListNewStructure } = useContext(IntegrationContext) as IntegrationContextPayload;

    const { newThreadInfo, flagAttachmentIconAnimation, setFlagAttachmentIconAnimation, checkIfChatIsExpiredForLeaveBtn, 
        flagRenderNewConversation, subType, currentActiveChatbotThread, clearAttachmentAnimationTimeout
    } = useRuleBasedInboxStore(useShallow((state) => ({
        newThreadInfo: state.newThreadInfo,
        flagAttachmentIconAnimation: state.flagAttachmentIconAnimation,
        setFlagAttachmentIconAnimation: state.setFlagAttachmentIconAnimation,
        checkIfChatIsExpiredForLeaveBtn: state.checkIfChatIsExpiredForLeaveBtn,
        flagRenderNewConversation: state.flagRenderNewConversation,
        subType: state.subType, 
        currentActiveChatbotThread: state.currentActiveChatbotThread,
        clearAttachmentAnimationTimeout: state.clearAttachmentAnimationTimeout
    }))) 

    const { showWarning, showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showWarning: state.showWarning,
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);  

    const [files, setFiles] = useState<any>([]);
    const hiddenFileInput = useRef<HTMLInputElement | null>(null);
    const [selectedFileType, setSelectedFileType] = useState<fileTypeInterface | null>(null);
    const [selectedMediaSet, setSelectedMediaSet] = useState<uploadedMedia[]>([]);

    const isChatExpired = checkIfChatIsExpiredForLeaveBtn(getManualOverrideConfig(currentActiveChatbotThread), true);

    const { isScreenBelow1000 } = useGenericHook();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if(!disabledAttchIcon()){
            setFlagAttachmentIconAnimation(false);
            clearAttachmentAnimationTimeout();
            setAnchorEl(event.currentTarget); 
            setSelectedFileType(null);
        }
    };

    const uploadFileHandler = async (e: any) => {
        for (var i = 0; i < e.target.files.length; i++) {
            const file: any = e.target.files[i];

            if((file?.size / 1048576) > getWhatsappMediaSizeLimit(selectedFileType?.label)){
                showWarning(`Please upload a file smaller than ${getWhatsappMediaSizeLimit(selectedFileType?.label)}MB`)
                e.target.value = null;
                continue;
            }

            file.isUploading = true;
            setFiles((prev: any) => {
                if(!prev) return [file];
                return [...prev, file];  
            })

            let formData = new FormData()
            formData.append(
                "file", file, file.name,
            )
            formData.append("chatbot_flag", "true");
            formData.append("private_file_flag", "false");

            setFlagLoading({ ...flagLoading, flagRoundLoader: true })
            await agent.uploadFile.post(formData).then(async (res) => {
                if (res.status) {
                    file.isUploading = false;
                    file.file_data = res.data.data;

                    file.google_storage_id = res.data.data.google_storage_id;
                    await setFiles((old: any) => {
                        let temp = [...old]
                        let fileType = file.name.split('.').pop()
                        let fileTypeIconSrc = getFileTypeImgIcon(fileType) ?? ""
                        temp.map((rr: any) => {
                            if (rr.google_storage_id == file.google_storage_id) {
                                rr.isUploading = false;
                                rr.file_data = res.data.data;
                                rr.google_storage_id = res.data.data.google_storage_id;
                                rr.previewImageSrc = fileTypeIconSrc;
                            }
                        })
                        return temp
                    })

                    const mediaObj: uploadedMedia = {
                        type: selectedFileType?.label?.toLowerCase() || "", 
                        url: res.data.data?.file_path, 
                        caption: "",
                        file_name: res.data.data?.google_storage_original_file_name,
                    }

                    setSelectedMediaSet((prev) => ( [...prev, mediaObj] ));
                    let images = [];
                    for (let i = 0; i < e.target.files.length; i++) {
                        images.push(URL.createObjectURL(e.target.files[i]))
                    }
                }
            }).catch((err) => {
                showError("Something went wrong!")
                setFiles([])
                console.error(err)
            });

            setFlagLoading({ ...flagLoading, flagRoundLoader: false })
        }
    } 

    const removeFileHandler = async (index: number) => {
        setFlagLoading({ ...flagLoading, flagRoundLoader: true })
        await agent.removeUploadedFile.delete(files[index].file_data.google_storage_id).then(async (res) => {
            if (res.data.status) {
                setFiles((prev: any) => {
                    return prev.filter((file: any, ind: number) => {
                        return ind != index;
                    })
                }); 
                setSelectedMediaSet((prev) => {
                    return prev.filter((media: uploadedMedia, ind: number) => {
                        return ind != index;
                    })
                })
                showSuccess("File Deleted Successfully")
            } else {
                handleError(res.data);
            }
        }).catch((err) => {
            console.log(err);
        })
        setFlagLoading({ ...flagLoading, flagRoundLoader: false })
    }

    const removeFilesInBg = async (files: any) => {
        for(let i = 0; i < files?.length; i++){
            await agent.removeUploadedFile.delete(files[i].file_data.google_storage_id).then(async (res) => {
                if(res.data.status){
                    setSelectedMediaSet((prev) => {
                        return prev.filter((media: uploadedMedia, ind: number) => {
                            return ind != i;
                        })
                    })
                }
            }).catch((err) => {
                console.log(err);
            })
        }
    }

    const onCloseOfMediaPreviewPopup = (files: any) => {
        removeFilesInBg(files); 
        setSelectedFileType(null); 
        setFiles(null); 
        setSelectedMediaSet([]);
    }

    const onChangeOfFieldType = (type: fileTypeInterface) => {
        if(type.label.toLowerCase() == "template"){
            if(currentActiveChatbotThread || flagRenderNewConversation){
                const isChatExpired = isUserChatExpired(currentActiveChatbotThread);
                const metaIntegrationWhatsAppId = getThreadData('metaIntegrationWhatsAppId', currentActiveChatbotThread, newThreadInfo, integrationListNewStructure) as number;
                getWATemplateList(metaIntegrationWhatsAppId, isChatExpired);
            }
            setAnchorEl(null);
        }else{ 
            if(!disabledAttchIcon(true)){
                setSelectedFileType(type);
            }
        }
    }  

    const uploadMedia = async () => {
        if (!checkInternetConnection()) {
            return;
        }
        setSelectedFileType(null); 
        setFiles([]); 
        await handleSendMessageManually("", true, false, false, selectedMediaSet, null);
        setSelectedMediaSet([]);
    }

    const disabledAttchIcon = (flagCheckIfChatIsExpired?: boolean) => {
        if(subType == "Closed" || !hasInboxReadAccess(apiRoutesPermissionAssignment, userDetail)){
            return true;
        } else {
            if(currentActiveChatbotThread){
                if(flagCheckIfChatIsExpired){
                    if(isUserChatExpired(currentActiveChatbotThread)){
                        return true;
                    }
                }

                let alreadyAssigned = currentActiveChatbotThread?.manual_agent_data?.find((agent: manualAgentData) => agent?.user_assignment_id == String(userDetail?.user_assignment_id))
                if(!alreadyAssigned && currentActiveChatbotThread?.manual_agent_data?.length > 0) return true;
                return false;
            } else {
                if(flagCheckIfChatIsExpired){
                    return flagRenderNewConversation;
                }
                return !flagRenderNewConversation;
            }
        }
    }

    useEffect(() => {
        if(selectedFileType){
            if(selectedMediaSet.length == 0){
                hiddenFileInput?.current?.click();
            }
            setAnchorEl(null)
        }
    }, [selectedFileType]) 

    const getAttachmentTooltipText = () => {
        if(checkIfChatIsExpiredForLeaveBtn(getManualOverrideConfig(currentActiveChatbotThread), true)){
            return "Send a marketing template"
        } 
        return "Attachment"
    } 

  return (
    <>
        <InputAdornment position="end" className={styles.fileUploadIconInputAdornment}>
            <input
                ref={hiddenFileInput}
                id="image"
                type="file"
                multiple
                className={`file-inputs ${styles.fileUploadIconInput}`}
                accept={selectedFileType?.acceptedFileType || ""}
                onChange={uploadFileHandler}
            >
            </input>

            <div onClick={(e) => handleClick(e)}>
                <GlobalTooltip
                    title={getAttachmentTooltipText()}
                    placement="top"
                    arrow
                    open={flagAttachmentIconAnimation ? true : undefined}
                    key={flagAttachmentIconAnimation ? "animation" : "no animation"}
                >
                    <IconButton
                        disabled={disabledAttchIcon()}
                        className={`chat-upload-icon-btn ${styles.mediaUploadIconButton}
                            ${isChatbotTextDirectionLtr(chatBotDetails) ? styles.attachmentIconMargin : styles.attachmentIconZeroMargin} 
                            ${disabledAttchIcon() ? styles.attchmentIconLowOpacity : styles.attchmentIconFullOpacity}
                        `}
                    >
                        <AttachFileIcon className={`${styles.mediaUploadIcon} ${flagAttachmentIconAnimation ? styles.heartbeatAnimationAttchmentIcon : ""}`}/>
                    </IconButton>
                </GlobalTooltip>
            </div>
            {(!isChatExpired && !flagRenderNewConversation) && 
                <CannedResponse
                    disabledAttchIcon={disabledAttchIcon}
                    handleSendMessageManually={handleSendMessageManually}
                />
            }
            <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                    'aria-labelledby': 'demo-customized-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
            > 
                <Grid container className={styles.fileTypeContainer}>
                        {fileType.map((type, ind) => {
                            return (
                                <Grid 
                                    item 
                                    key={ind} 
                                    xs={12} 
                                    className={`${styles.fileTypeGridItem} ${(type.label.toLowerCase() != ATTACHMENT_TEMPLATE_LABEL_TEXT?.toLowerCase() && disabledAttchIcon(true)) ? styles.fileTypeGridItemDisabled :  "" }`} 
                                    onClick={() => onChangeOfFieldType(type)}
                                >
                                    <Grid container className={styles.fileItemContainer}>
                                        <Grid item className={styles.fileTypeIcon}>
                                            {React.cloneElement(type.icon)}
                                        </Grid>
                                        <Grid item> 
                                            <Typography className={styles.fileTypeLabel}>{type.label}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })}
                    </Grid>
            </StyledMenu>
        </InputAdornment>

        {(selectedFileType && selectedMediaSet.length > 0) && 
            <GlobalPopUp
                open={selectedFileType.label?.length > 0 && selectedMediaSet.length > 0}
                onClose={() => onCloseOfMediaPreviewPopup(files)}
                handleClose={() => onCloseOfMediaPreviewPopup(files)}
                flagShowCloseIcon={true}
                paperMinWidth={isScreenBelow1000 ? "410px" : "850px"}
                paperMaxwidth={isScreenBelow1000 ? "710px" : "850px"}
                borderRadius='10px'
                titlePadding='0px'
            >
                <FileUploadPopup
                    selectedMediaSet={selectedMediaSet}
                    setSelectedMediaSet={setSelectedMediaSet}
                    hiddenFileInput={hiddenFileInput}
                    removeFileHandler={removeFileHandler}
                    uploadMedia={uploadMedia}
                />
            </GlobalPopUp>
        }
    </>
  )
}

export default memo(FileUploadInbox);
